import { useMemo } from 'react';
import { Badge, Chip, TagsInput, useResourceList, type WidgetProps } from '../../engine';

// CRM field widgets (FieldMeta.widget → ModuleUI.widgets):
//   email-domains  — text[] of sender domains: chips to read, a tag editor that normalises
//                    ('@Acme.COM' → 'acme.com') and rejects non-domains.
//   client-types   — uuid[] mirrored from client_type_link: coloured chips by name; toggle chips
//                    to edit (the API replaces the links from `client_type_ids`).
//   contact-roles  — uuid[] mirrored from contact_role_link, same shape.

const DOMAIN_RE = /^[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,}$/;

export function normalizeDomain(raw: string): string | null {
  const d = raw.trim().toLowerCase().replace(/^@/, '').replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0] ?? '';
  return DOMAIN_RE.test(d) ? d : null;
}

export function EmailDomainsField({ mode, value, onChange, disabled, id, 'aria-label': ariaLabel, field }: WidgetProps) {
  const domains = Array.isArray(value) ? (value as string[]) : [];
  if (mode === 'render') {
    if (!domains.length) return <span className="hf-muted">—</span>;
    return (
      <span className="hf-chiprow">
        {domains.map((d) => (
          <Chip key={d} icon="globe">
            {d}
          </Chip>
        ))}
      </span>
    );
  }
  return (
    <TagsInput
      id={id}
      value={domains}
      suggest={false}
      disabled={disabled}
      placeholder="example.com"
      aria-label={ariaLabel ?? field.label}
      onChange={(next) => onChange?.([...new Set(next.map(normalizeDomain).filter((d): d is string => Boolean(d)))])}
    />
  );
}

interface CatalogRow {
  id: string;
  name: string;
  color?: string | null;
  is_active?: boolean;
}

/** Chips resolved from a tenant catalog resource; toggles in edit mode. */
function CatalogChips({ resource, mode, value, onChange, disabled, field }: WidgetProps & { resource: string }) {
  const ids = Array.isArray(value) ? (value as string[]) : [];
  const list = useResourceList<CatalogRow>(resource, { sort: 'sort_order', limit: 100 }, { staleTime: 60_000 });
  const byId = useMemo(() => new Map(list.rows.map((r) => [r.id, r])), [list.rows]);
  if (mode === 'render') {
    if (!ids.length) return <span className="hf-muted">—</span>;
    return (
      <span className="hf-chiprow">
        {ids.map((id) => {
          const row = byId.get(id);
          return (
            <Badge key={id} tone="accent" dot={false} className="hf-catalog-chip" title={row?.name}>
              {row?.color && <span className="hf-swatch" style={{ background: row.color }} aria-hidden />}
              {row?.name ?? '…'}
            </Badge>
          );
        })}
      </span>
    );
  }
  const options = list.rows.filter((r) => r.is_active !== false || ids.includes(r.id));
  return (
    <div className="hf-chiprow" role="group" aria-label={field.label}>
      {options.map((r) => {
        const on = ids.includes(r.id);
        return (
          <Chip key={r.id} role="checkbox" aria-selected={on} active={on} disabled={disabled} onClick={() => onChange?.(on ? ids.filter((x) => x !== r.id) : [...ids, r.id])}>
            {r.color && <span className="hf-swatch" style={{ background: r.color }} aria-hidden />}
            {r.name}
          </Chip>
        );
      })}
      {options.length === 0 && <span className="hf-muted">{list.isLoading ? 'Loading…' : 'None defined'}</span>}
    </div>
  );
}

export const ClientTypesField = (p: WidgetProps) => <CatalogChips {...p} resource="client-types" />;
export const ContactRolesField = (p: WidgetProps) => <CatalogChips {...p} resource="contact-roles" />;
